import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Link } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { apiFetch, queryString } from "../lib/api";
import { classifyUtilization, type UtilizationTier } from "../lib/domain";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "../components/ui";

interface RoomSummary {
  campus: string;
  room: string;
  roomCapacity: number;
  bookingCount: number;
  averageUtilization: number;
  peakUtilization: number;
  scheduledEnrollment: number;
}

const tierColor: Record<UtilizationTier, string> = {
  "critical-under-utilization": "bg-amber-500",
  "below-optimal": "bg-sky-500",
  optimal: "bg-emerald-500",
  "near-capacity": "bg-violet-500",
  "over-capacity": "bg-rose-500",
};

export function RoomsPage() {
  const { snapshotTermId, departmentId } = useAppContext();
  const [search, setSearch] = useState("");
  const [campus, setCampus] = useState("");
  const [tier, setTier] = useState<UtilizationTier | "">("");
  const query = useQuery({
    queryKey: ["rooms", snapshotTermId, departmentId],
    queryFn: () =>
      apiFetch<RoomSummary[]>(
        `/rooms${queryString({ snapshotTermId, department: departmentId })}`,
      ),
    enabled: Boolean(snapshotTermId),
  });
  const campuses = useMemo(
    () =>
      [...new Set((query.data ?? []).map((room) => room.campus))]
        .filter(Boolean)
        .sort(),
    [query.data],
  );
  const rooms = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return (query.data ?? []).filter((room) => {
      const matchesSearch =
        !normalizedSearch ||
        room.room.toLowerCase().includes(normalizedSearch) ||
        room.campus.toLowerCase().includes(normalizedSearch);
      return (
        matchesSearch &&
        (!campus || room.campus === campus) &&
        (!tier || classifyUtilization(room.averageUtilization) === tier)
      );
    });
  }, [campus, query.data, search, tier]);

  if (query.isLoading) return <LoadingState />;
  if (query.error) return <ErrorState error={query.error} />;

  return (
    <>
      <PageHeader
        title="Room Occupancy Audit"
        description="Compact booking-level utilization based on scheduled maximum enrollment and room capacity."
      />
      <div className="panel p-4">
        <div className="grid gap-3 md:grid-cols-[1fr_220px_240px]">
          <label className="relative">
            <span className="sr-only">Search rooms</span>
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              className="field w-full pl-9"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search room or campus"
            />
          </label>
          <select className="field" value={campus} onChange={(event) => setCampus(event.target.value)}>
            <option value="">All campuses</option>
            {campuses.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
          <select
            className="field"
            value={tier}
            onChange={(event) => setTier(event.target.value as UtilizationTier | "")}
          >
            <option value="">All utilization tiers</option>
            <option value="critical-under-utilization">Critical under-utilization</option>
            <option value="below-optimal">Below optimal</option>
            <option value="optimal">Optimal</option>
            <option value="near-capacity">Near capacity</option>
            <option value="over-capacity">Over capacity</option>
          </select>
        </div>
      </div>
      {rooms.length === 0 ? (
        <EmptyState
          title="No matching physical rooms"
          description="Adjust the room, campus, utilization, or global department filters."
        />
      ) : (
        <div className="panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[850px] text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">Room</th>
                  <th className="px-4 py-3">Campus</th>
                  <th className="px-4 py-3 text-right">Capacity</th>
                  <th className="px-4 py-3 text-right">Bookings</th>
                  <th className="px-4 py-3 text-right">Enrollment</th>
                  <th className="px-4 py-3 text-right">Average</th>
                  <th className="px-4 py-3 text-right">Peak</th>
                  <th className="px-4 py-3">Utilization</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rooms.map((room) => {
                  const roomTier = classifyUtilization(room.averageUtilization);
                  return (
                    <tr key={`${room.campus}:${room.room}`} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5">
                        <Link
                          className="font-bold text-brand-700 hover:underline"
                          to={`/timetable?room=${encodeURIComponent(room.room)}`}
                        >
                          {room.room}
                        </Link>
                      </td>
                      <td className="px-4 py-2.5 text-slate-600">{room.campus}</td>
                      <td className="px-4 py-2.5 text-right">{room.roomCapacity}</td>
                      <td className="px-4 py-2.5 text-right">{room.bookingCount}</td>
                      <td className="px-4 py-2.5 text-right">{room.scheduledEnrollment}</td>
                      <td className="px-4 py-2.5 text-right font-bold">{(room.averageUtilization * 100).toFixed(1)}%</td>
                      <td className="px-4 py-2.5 text-right">{(room.peakUtilization * 100).toFixed(0)}%</td>
                      <td className="w-56 px-4 py-2.5">
                        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                          <div
                            className={`h-full ${tierColor[roomTier]}`}
                            style={{ width: `${Math.min(room.averageUtilization * 100, 100)}%` }}
                          />
                        </div>
                        <p className="mt-1 text-[10px] capitalize text-slate-500">{roomTier.replaceAll("-", " ")}</p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="border-t border-slate-200 px-4 py-3 text-xs text-slate-500">
            {rooms.length} of {query.data?.length ?? 0} rooms shown
          </p>
        </div>
      )}
    </>
  );
}
